import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { AuthContext } from '@repo-level/core-front';
import { FullInformation } from 'components/auth/FullInformation';
import { Login } from 'components/auth/Login';
import { Recovery } from 'components/auth/Recovery';
import { Registration } from 'components/auth/Registration';
import { Welcome } from 'components/auth/Welcome';
import { Deposit } from 'components/Deposit';
import { Support } from 'components/general/Support';
import { EasterWonderland } from 'components/modals/EasterWonderland';
import { Modal, ModalDefaultProps } from 'components/Modal';
import {
  addUrlParameter,
  getQueryParameter,
  removeUrlParameters,
} from 'helpers/common';
import { EditCurrencies } from 'components/Deposit/Modals/EditCurrencies';
import { TabbarMenuModal } from 'components/general/Tabbar/TabbarMenuModal';
import { MyCurrencies } from 'components/Deposit/Modals/MyCurrencies';
import { CurrencyRemovalDeclined } from 'components/Deposit/Modals/CurrencyRemovalDeclined';
import { BirthdayModal } from 'components/modals/BirthdayModal';
import { JackpotTCModal } from 'components/modals/JackpotModals/JackpotTCModal';
import { WinnerJackpotModal } from 'components/modals/JackpotModals/WinnerJackpotModal';
import { AutoRegistration } from 'components/auth/AutoRegistration';
import { EditPasswordModal } from 'modules/user/password/edit/EditPasswordModal';
import { WithdrawModal } from 'components/modals/WithdrawModal';
import { NetworkModal } from 'components/modals/NetworkModal';
import { ExchangeCompointsModal } from 'components/modals/ExchangeCompointsModal';
import { NewTermsAndConditionsModal } from 'components/modals/NewTermsAndConditionsModal';
import { MobileAppModal } from 'components/modals/MobileAppModal';

export enum ModalType {
  Registration = 'registration',
  AutoRegistration = 'auto-registration',
  Login = 'login',
  NewTermsAndConditions = 'newTermsAndConditions',
  MobileApp = 'MobileAppModal',
  Deposit = 'deposit',
  EditCurrencies = 'editCurrencies',
  Recovery = 'recovery',
  Support = 'support',
  FullInformation = 'full-information',
  Welcome = 'welcome',
  EasterWonderland = 'easterWonderland',
  TabbarMenu = 'menu',
  MyCurrencies = 'myCurrencies',
  CurrencyRemovalDeclined = 'removal-declined',
  Birthday = 'birthday',
  JackpotTC = 'jackpotTC',
  WinnerJackpot = 'winnerJackpot',
  EditPassword = 'editPassword',
  ExchangeCompoints = 'exchangeCompoints',
  Withdraw = 'withdraw',
  NetworkModal = 'networkModal',
}

export const nonAuthModalsMap: string[] = [
  ModalType.Registration,
  ModalType.Login,
  ModalType.AutoRegistration,
];

type ModalComponent = React.ForwardRefExoticComponent<
  ModalDefaultProps & React.RefAttributes<HTMLDivElement>
>;

type ModalsState = {
  name: string;
  component: ModalComponent;
};

const modalsMap: {
  [key in ModalType]: { component: ModalComponent };
} = {
  [ModalType.Registration]: { component: Registration },
  [ModalType.AutoRegistration]: { component: AutoRegistration },
  [ModalType.Login]: { component: Login },
  [ModalType.NewTermsAndConditions]: { component: NewTermsAndConditionsModal },
  [ModalType.MobileApp]: { component: MobileAppModal },
  [ModalType.Deposit]: { component: Deposit },
  [ModalType.Recovery]: { component: Recovery },
  [ModalType.EditCurrencies]: { component: EditCurrencies },
  [ModalType.Support]: { component: Support },
  [ModalType.FullInformation]: { component: FullInformation },
  [ModalType.Welcome]: { component: Welcome },
  [ModalType.EasterWonderland]: { component: EasterWonderland },
  [ModalType.TabbarMenu]: { component: TabbarMenuModal },
  [ModalType.MyCurrencies]: { component: MyCurrencies },
  [ModalType.CurrencyRemovalDeclined]: { component: CurrencyRemovalDeclined },
  [ModalType.Birthday]: { component: BirthdayModal },
  [ModalType.JackpotTC]: { component: JackpotTCModal },
  [ModalType.WinnerJackpot]: { component: WinnerJackpotModal },
  [ModalType.EditPassword]: { component: EditPasswordModal },
  [ModalType.ExchangeCompoints]: { component: ExchangeCompointsModal },
  [ModalType.Withdraw]: { component: WithdrawModal },
  [ModalType.NetworkModal]: { component: NetworkModal },
};

interface ModalsContextData {
  modalComponent: JSX.Element | null;
  modals: ModalsState[];
}

type HandleModalFn = (
  modal: ModalType | ModalComponent | undefined,
  params?: {
    name?: string;
    stayInSameWindow?: boolean;
  }
) => () => void;

interface ModalsContextAction {
  handleModal: HandleModalFn;
}

export const ModalsContext = createContext<
  ModalsContextData & ModalsContextAction
>(null as any);

function addBreadcrumbModal(name: string, status: 'open' | 'close') {}

export const ModalsContextProvider: FC = ({ children }) => {
  const { isAuthenticated, pendingUser } = useContext(AuthContext);
  const [modals, setModals] = useState<ModalsState[]>([]);

  const handleModal = useCallback<HandleModalFn>(
    (modal, params) => () => {
      if (modal == null) {
        setModals((state) => {
          if (!state.length) return [];

          addBreadcrumbModal(state[state.length - 1].name, 'close');

          const updated = state.slice(0, -1);

          const last = updated[updated.length - 1];

          if (!!last) {
            addUrlParameter('modal', last.name);
          } else {
            removeUrlParameters(['modal']);
          }

          return state.slice(0, -1);
        });
        return;
      }

      addBreadcrumbModal(
        typeof modal === 'string' ? modal : params?.name || 'custom modal',
        'open'
      );

      if (typeof modal === 'string') {
        addUrlParameter('modal', modal);
        const { component } = modalsMap[modal as ModalType];
        setModals((state) =>
          params?.stayInSameWindow
            ? [...state, { name: modal, component }]
            : [{ name: modal, component }]
        );
        return;
      }

      if (params?.name) addUrlParameter('modal', params.name);

      setModals((state) =>
        params?.stayInSameWindow
          ? [...state, { name: params?.name || 'custom', component: modal }]
          : [{ name: params?.name || '', component: modal }]
      );
    },
    [setModals]
  );

  const handleClose = useCallback(() => {
    handleModal(undefined)();
  }, [handleModal]);

  useEffect(() => {
    if (!modals.length) {
      const modalParam = getQueryParameter('modal');
      const isNonAuthModal = nonAuthModalsMap.includes(modalParam || '');

      if (isAuthenticated && isNonAuthModal) {
        removeUrlParameters(['modal']);
        return;
      }

      const modalType =
        modalParam && Object.values(ModalType).includes(modalParam as ModalType)
          ? (modalParam as ModalType)
          : undefined;

      if (modalType && !pendingUser) {
        handleModal(modalType)();
      }
    }
  }, [handleModal, isAuthenticated, modals.length, pendingUser]);

  const modalComponent = useMemo(() => {
    return modals.length ? (
      <>
        {modals.map(({ component: ModalContent }, index) => (
          <Modal key={index} onClose={handleClose}>
            {(props) => <ModalContent {...props} />}
          </Modal>
        ))}
      </>
    ) : null;
  }, [modals, handleClose]);

  return (
    <ModalsContext.Provider value={{ modalComponent, handleModal, modals }}>
      {children}
    </ModalsContext.Provider>
  );
};
